<template>
	<div class="support-calculator-tab">
		<support-calculator
			:province="provinceSnapshot"
			:payor-name-prop="payorNameSnapshot"
			:recipient-name-prop="recipientNameSnapshot"
			:cohabitation-date-prop="cohabProp"
			:separation-date-prop="sepProp"
			:payor-d-o-b-prop="payorDOBSnapshot"
			:recipient-d-o-b-prop="recipientDOBSnapshot"
			:children-prop="childrenSnapshot"
			:initial-state="initialStateSnapshot"
			:matter-context="matterContext"
			@update-state="onUpdateState"
			@apply="onApply"
		/>
	</div>
</template>

<script>
// Thin host wrapper around the lawyer-supplied support calculator. It maps the
// matter's party/relationship data onto the calculator's prop contract (see the
// calculator's INTEGRATION.md) and persists the calculator's working state on
// the agreement so it survives a reload.
//
// ⚠ SupportCalculator.vue is NO LONGER a verbatim copy. It carries deliberate
// Divii-only deltas on top of the upstream drop (currently source commit
// 0cebce2 of LegalAppVic/support-calculator, branch vue2-port):
//   1. the builder→calculator prop-sync engine (runPropSync /
//      computePropSyncPlan / syncConflicts — see NOTE(sync-reconciliation))
//   2. scrollPageToTop scroll-reset on viewMode/step changes
//   3. the restructure empty-state
//   4. "Federal Tables" wording
//   5. swapParties() also swapping payor/recipient DOBs
//   6. TAX_YEAR_OPTIONS narrowed to [2026, 2025]
//   7. upstream's dev-only window.__diviiAudit block deleted (its Vite-only
//      env guard cannot be parsed by webpack 4 — hard build error here)
//   8. created() validates the restored workspaceTab against what the current
//      role can RENDER (search "DIVII FIX") — without it, a state saved by a
//      level-2 session reopens blank for everyone else
//   9. accessProfile() honours matterContext.readOnly → 'view', so the User
//      Role page's support-calculator Edit permission can make it read-only
// NEVER overwrite the file with an upstream copy: do a 3-way merge with the
// last synced upstream commit as the base, or all of the above is silently
// reverted. See the plan/ADR for the exact procedure.
import debounce from 'lodash/debounce';
import { auth } from '../../firebase';
import { hasEditPermission } from '../../mixins/roleLevelPermissions';
import SupportCalculator from './SupportCalculator.vue';

// Roles the calculator renders entirely read-only (Separating Party level 3/4).
// They never legitimately write, but the calculator's created() assigns
// viewMode/workspaceTab for read-only profiles, which trips the viewMode watcher
// -> emitStateUpdate -> onUpdateState. On a matter with no saved state there is
// no _isHydrating guard to swallow it, so a view-only user would otherwise
// create the first supportCalculatorState. Level-2 and sharing users are
// deliberately NOT here — they legitimately enter their own income and budget.
const VIEW_ONLY_ROLES = ['client-level-3', 'client-level-4'];

// Full province/territory name (as stored on the agreement) → the
// jurisdiction codes the calculator understands.
const PROVINCE_CODES = {
	'british columbia': 'BC',
	alberta: 'AB',
	saskatchewan: 'SK',
	manitoba: 'MB',
	ontario: 'ON',
	quebec: 'QC',
	'new brunswick': 'NB',
	'nova scotia': 'NS',
	'prince edward island': 'PE',
	'newfoundland and labrador': 'NL',
	newfoundland: 'NL',
	yukon: 'YT',
	'northwest territories': 'NT',
	nunavut: 'NU',
};

function provinceCode(name) {
	if (!name) return null;
	return PROVINCE_CODES[String(name).trim().toLowerCase()] || null;
}

// Stored dates are ISO timestamps ("1981-02-02T08:00:00.000Z"); the calculator
// wants "yyyy-mm-dd". We must derive the LOCAL calendar date, not slice the UTC
// portion: the builder saves dates as a locally-parsed date -> toISOString(), and
// displays them back in local time. Slicing the UTC string shifts the day by one
// for timezones ahead of/behind UTC (e.g. IST turns 1995-01-01 into 1994-12-31).
function toIsoDate(value) {
	if (!value) return null;
	const s = String(value);
	// Already a plain calendar date (no time component) — pass through untouched.
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
	const d = new Date(s);
	if (isNaN(d.getTime())) return s.length >= 10 ? s.slice(0, 10) : null;
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

// Display rule: preferred name when set, else "first last". The agreement text
// refers to parties/children by preferred name throughout (see preferredName()
// in @/lib/agreement/filters.js — not reused here because it lacks the
// last-name fallback), so the calculator should match.
function partnerName(partner) {
	if (!partner || !partner.name) return null;
	const preferred = String(partner.name.preferred || '').trim();
	if (preferred) return preferred;
	const name = [partner.name.first, partner.name.last].filter(Boolean).join(' ').trim();
	return name || null;
}

export default {
	name: 'SupportCalculatorTab',
	components: { SupportCalculator },
	props: {
		agreementData: { type: Object, required: true },
	},
	computed: {
		// Relationship dates stay live (reactive), not snapshotted, so the
		// calculator always reflects the builder's current values. The builder
		// owns these dates.
		cohabProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.cohabitationDate || r.marriageDate || '');
		},
		sepProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.separationDate || '');
		},
		// The signed-in user's role ON THIS AGREEMENT — not their global role.
		// Resolved by allPermissionsAvailable() (mixins/roleLevelPermissions.js),
		// which matches the auth email against the agreement's users/permissions
		// doc. The calculator maps the string through its own ROLE_PROFILES table
		// to one of four access profiles.
		// Deliberately NOT store.state.currentRoleLevel: that is the GLOBAL role
		// from customers/{uid}.roleLevel, and the same person can hold different
		// levels on different agreements.
		roleOnAgreement() {
			return this.$store.state.currentAgreementRoleLevelName || null;
		},
		// Stable per-ACCOUNT id for party self-identification. The calculator's
		// party picker stores a claim keyed by this, so the same person keeps
		// their party across devices and sessions.
		// This MUST be passed: with no userId the calculator silently falls back
		// to a per-browser localStorage token (currentUserKey), and the claim
		// then only holds on the device that made it.
		// NOTE: we no longer try to work out the party ourselves. The previous
		// approach matched the auth email against agreement.partner{1,2}.mail,
		// but that is an optional free-text contact field — filled on 1 of 10
		// sampled matters, and not necessarily the address the person signs in
		// with. Upstream now fails closed (rolePartyId returns null rather than
		// guessing party2) and asks the user directly, so we deliberately omit
		// userPartySlot and let the in-tool picker decide.
		userId() {
			const uid = auth.currentUser && auth.currentUser.uid;
			return uid ? String(uid) : null;
		},
		// Display name for the signed-in user. Only used to label who is signed
		// in and to attribute self-reported entries — it never affects access.
		userName() {
			const u = auth.currentUser || {};
			const store = this.$store.state.user || {};
			const name = String(u.displayName || store.displayName || '').trim();
			return name || String(u.email || store.email || '').trim() || null;
		},
		// Role context only. Every identity/prefill field is deliberately absent:
		// the calculator's fromCtx()/pick() give matterContext precedence OVER
		// the flat props, so duplicating the prefill here would shadow it and
		// disturb the builder→calculator sync engine for no gain. Absent keys
		// fall through to the props above.
		// Passed unconditionally — the calculator grants FULL access when `role`
		// is missing, so a conditional skip would silently un-restrict a
		// view-only user. A computed (not a data() snapshot) keeps these live on
		// every open, as the integration doc requires.
		readOnly() {
			return !hasEditPermission('support-calculator');
		},
		matterContext() {
			return { role: this.roleOnAgreement, readOnly: this.readOnly, userId: this.userId, userName: this.userName };
		},
	},
	data() {
		// Snapshot the inputs ONCE at creation. Persisting state re-fetches the
		// agreement (see updateAgreementFields), which would otherwise flow back
		// in as new props and reset the calculator mid-edit. The tab is rendered
		// behind a v-if, so it is recreated (and re-snapshotted from fresh matter
		// data) each time it is opened.
		const ad = this.agreementData || {};
		// "Province of agreement" from Matter Details — not the partners'
		// address province — decides the support jurisdiction.
		const seedProvince =
			(ad.agreement && ad.agreement.updatedProvinceOfAgreement) ||
			(ad.agreement && ad.agreement.provinceOfAgreement) ||
			ad.provinceOfAgreement ||
			'';
		// Default partner1 as payor / partner2 as recipient; the calculator lets
		// the lawyer swap them and persists that choice in its own state.
		const children = (ad.children || [])
			.map((child) => ({
				// Stable builder id (ChildInformation generates/backfills it) —
				// lets the calculator's sync match children exactly across
				// renames and DOB edits. Null for children saved before ids.
				id: child.id || null,
				name: partnerName(child) || '',
				dob: toIsoDate(child.birthdate),
			}))
			.filter((c) => c.name || c.dob);
		return {
			provinceSnapshot: provinceCode(seedProvince),
			payorNameSnapshot: partnerName(ad.partner1),
			recipientNameSnapshot: partnerName(ad.partner2),
			payorDOBSnapshot: toIsoDate(ad.partner1 && ad.partner1.birthdate),
			recipientDOBSnapshot: toIsoDate(ad.partner2 && ad.partner2.birthdate),
			childrenSnapshot: children,
			initialStateSnapshot: ad.supportCalculatorState || null,
		};
	},
	created() {
		// Guard against redundant writes: the calculator emits update-state on
		// load with the restored state, and we only want to persist real changes.
		this.lastPersistedJson = JSON.stringify(this.agreementData.supportCalculatorState || null);
		// The calculator debounces update-state ~400ms itself; debounce again
		// before hitting Firestore, matching the pension calculator's cadence.
		this.persist = debounce((state) => {
			// UI gating is advisory; this is the one place every calculator write
			// funnels through, so view-only roles are stopped here.
			if (VIEW_ONLY_ROLES.includes(this.roleOnAgreement) || this.readOnly) return;
			const json = JSON.stringify(state);
			if (json === this.lastPersistedJson) return;
			const id = this.$store.state.agreement && this.$store.state.agreement.id;
			if (!id) return;
			this.lastPersistedJson = json;
			this.$store.dispatch('updateAgreementFields', {
				id,
				fields: { supportCalculatorState: state },
			});
		}, 800);
	},
	beforeDestroy() {
		if (this.persist) {
			this.persist.flush(); // write the final edit before the tab is torn down
			this.persist.cancel();
		}
	},
	methods: {
		onUpdateState(state) {
			this.persist(state);
		},
		onApply() {
			// Gated off in calculator v1 (applyToAgreementEnabled: false). v2 will
			// deliver the frozen apply payload here to write into the agreement.
		},
	},
};
</script>

<style scoped>
.support-calculator-tab {
	width: 100%;
}
</style>
